import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, watch } from 'vue';
import { useToken } from '@/useToken';
import { useUser } from '@/useUser';
import { AjaxHelper } from '@samc/common';
import { CellRendererSelectorResult, GridOptions, Returnable } from '@samc/common-ui';
import { WorkflowTemplateUserDataSource } from '../Sources/WorkflowTemplateUserSource';
import { TaskTypesSource } from '@/Sources/TaskTypesSource';
import { DomainLimitsSource, filterLimitRowsBySearch } from '../Sources/DomainLimitsSource';
import { ValidationTemplatesSource } from '../Sources/ValidationTemplatesSource';
import { TaskGroupsSource } from '@/Sources/TaskGroupsSource';
import { buildTaskTypePickerRows } from '@/utils/taskTypePickerRows';
import { TaskTypePickerCellEditor } from './TaskTypePickerCellEditor';
import Entitlements from '@/Entitlements';
import {
  ACQUISITION_PACKAGE_STATUSES,
  DEAL_PACKAGE_STATUSES
} from '@/Types';

interface Validations {
  templateName?: string;
  description?: string;
  templateitems?: string;
  [key: string]: string | undefined;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkflowTemplateForm',
  props: {
    template: { type: null, required: true },
    verificationToken: { type: String, required: true },
    apiRoot: { type: String, required: true }
  },
  emits: ["saved", "cancelled"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'saved'): void,
  (e: 'cancelled'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  template: ITaskTypeTemplate | null,
  verificationToken: string,
  apiRoot: string
};

function _blankTemplate(): ITaskTypeTemplate {
  return {
    id: undefined,
    templateName: undefined,
    description: undefined,
    isDeal: false,
    taskTypeTemplateItems: []
  };
}

/** Parent sends `{}` for new templates; treat that as blank so `isDeal` and other defaults exist. */
function createInitialTemplate(template: ITaskTypeTemplate | null | undefined): ITaskTypeTemplate {
  if (!template || Object.keys(template).length === 0) {
    return _blankTemplate();
  }
  return {
    ..._blankTemplate(),
    ...structuredClone(template),
    isDeal: Boolean(template.isDeal)
  };
}





const { token } = useToken();
const { user } = useUser();

const showValidations = ref(false);
const validations = ref<Validations>({}); 
const templateData = ref<ITaskTypeTemplate>(createInitialTemplate(props.template));
const templateItems = ref<ITaskTypeTemplateItem[]>(
  props.template?.taskTypeTemplateItems ? structuredClone(props.template.taskTypeTemplateItems) : []
);

const taskTypesSource = ref(new TaskTypesSource(props.apiRoot, token.value, props.verificationToken));
const taskGroupsSource = ref(new TaskGroupsSource(props.apiRoot, token.value, props.verificationToken));
const userSource = ref(new WorkflowTemplateUserDataSource());

const gridRef = ref<HTMLSamcGridElement | null>(null);

const domainLimitsSource = ref<DomainLimitsSource | null>(null);
const limitsPickerLoaded = ref(false);
const limitSearchTerm = ref('');

const validationTemplatesSource = ref<ValidationTemplatesSource | null>(null);
const validationsPickerLoaded = ref(false);
const validationSearchTerm = ref('');

const getStatusOptions = computed(() => {
  const isDeal = Boolean(templateData.value.isDeal);
  const statuses: readonly (string | undefined)[] = [
    undefined,
    ...(isDeal ? DEAL_PACKAGE_STATUSES : ACQUISITION_PACKAGE_STATUSES)
  ];
  return statuses.map(status => ({
    text: status ?? 'Not specified',
    value: status
  }));
});

const statusEditorParams = computed(() => ({
  source: getStatusOptions.value,
  width: '100%',
  minWidth: 200
}));

const isNew = computed(() => templateData.value.id === undefined || templateData.value.id === null || templateData.value.id === 0);
const hasErrors = computed(() => Object.keys(validations.value).length > 0);

const templateNameError = computed(() => {
  if (showValidations.value !== undefined) {
    if (validations.value.templateName === undefined) {
      return '';
    }
    return validations.value?.templateName;
  }
  return '';
});

const descriptionError = computed(() => {
  if (showValidations.value !== undefined) {
    if (validations.value.description === undefined) {
      return '';
    }
    return validations.value?.description;
  }
  return '';
});

const templateItemsError = computed(() => {
  if (showValidations.value !== undefined) {
    if (validations.value.templateitems === undefined) {
      return '';
    }
    return validations.value?.templateitems;
  }
  return '';
});

const entitledToCreateTaskTemplates = computed(() => user.value?.hasEntitlement(Entitlements.TaskManagementModule.CreateTaskTemplates) ?? false);
const entitledToEditOrRemoveTaskTemplates = computed(() => user.value?.hasEntitlement(Entitlements.TaskManagementModule.EditOrRemoveTaskTemplates) ?? false);

const canEdit = computed(() => (isNew.value && entitledToCreateTaskTemplates.value) || (!isNew.value && entitledToEditOrRemoveTaskTemplates.value));

const taskTypePickerRows = computed(() =>
  buildTaskTypePickerRows(
    taskGroupsSource.value.cachedItems,
    taskTypesSource.value.cachedItems
  )
);

const taskTypeIdToLabel = computed(() => {
  const labels = new Map<number, string>();
  for (const row of taskTypePickerRows.value) {
    if (row.kind === 'taskType') {
      labels.set(row.id, row.text);
    }
  }
  return labels;
});

const domainSetPickerApiRoot = computed(() => {
  const w = typeof window !== 'undefined' ? (window as unknown as { __WEBAPI_URL?: string }) : undefined;
  const qb = w?.__WEBAPI_URL?.trim();
  return qb && qb.length > 0 ? qb : props.apiRoot;
});

const limitsPickerRows = computed(() => domainLimitsSource.value?.cachedItems ?? []);

const filteredLimitPickerRows = computed(() =>
  filterLimitRowsBySearch(limitsPickerRows.value, limitSearchTerm.value)
);

const limitGuidToLabel = computed(() => {
  const m = new Map<string, string>();
  for (const r of limitsPickerRows.value) {
    if (r.kind === 'limit') {
      m.set(r.guid.toLowerCase(), r.text);
    }
  }
  return m;
});

function limitLabelForGuid(guid: string): string {
  return limitGuidToLabel.value.get(guid.trim().toLowerCase()) ?? guid;
}

const validationPickerRows = computed(() => validationTemplatesSource.value?.cachedItems ?? []);

const filteredValidationPickerRows = computed(() =>
  filterLimitRowsBySearch(validationPickerRows.value, validationSearchTerm.value)
);

const validationGuidToLabel = computed(() => {
  const m = new Map<string, string>();
  for (const r of validationPickerRows.value) {
    if (r.kind === 'limit') {
      m.set(r.guid.toLowerCase(), r.text);
    }
  }
  return m;
});

function validationLabelForGuid(guid: string): string {
  return validationGuidToLabel.value.get(guid.trim().toLowerCase()) ?? guid;
}

const validationLimitModalOpen = ref(false);
const validationLimitEditingIndex = ref(-1);
const validationLimitKindForModal = ref<'validation' | 'limit'>('validation');
const modalValidationTemplateGuid = ref<string | undefined>(undefined);
const modalLimitSetId = ref<string | undefined>(undefined);

const formatValidationLimitSummary = (item: ITaskTypeTemplateItem | undefined): string => {
  if (item === undefined || item === null) {
    return '';
  }
  if (item.createValidationTemplateId !== null && item.createValidationTemplateId !== undefined) {
    return `V ${validationLabelForGuid(item.createValidationTemplateId)}`;
  }
  if (item.createLimitSetId !== null && item.createLimitSetId !== undefined) {
    return `L ${limitLabelForGuid(item.createLimitSetId)}`;
  }
  return '';
};

const openValidationLimitModal = (rowIndex: number) => {
  const item = templateItems.value[rowIndex];
  if (!item) {
    return;
  }
  validationLimitEditingIndex.value = rowIndex;
  validationLimitKindForModal.value =
    item.openRequirementKind ?? (item.createLimitSetId ? 'limit' : 'validation');
  modalValidationTemplateGuid.value = item.createValidationTemplateId
    ? item.createValidationTemplateId.trim().toUpperCase()
    : undefined;
  modalLimitSetId.value = item.createLimitSetId
    ? item.createLimitSetId.trim().toUpperCase()
    : undefined;
  limitSearchTerm.value = '';
  validationSearchTerm.value = '';
  validationLimitModalOpen.value = true;
};

const closeValidationLimitModal = () => {
  validationLimitModalOpen.value = false;
  validationLimitEditingIndex.value = -1;
  limitSearchTerm.value = '';
  validationSearchTerm.value = '';
};

const applyValidationLimitModal = () => {
  const idx = validationLimitEditingIndex.value;
  const item = templateItems.value[idx];
  if (!item || idx < 0) {
    closeValidationLimitModal();
    return;
  }
  item.openRequirementKind = validationLimitKindForModal.value;
  if (validationLimitKindForModal.value === 'validation') {
    item.createValidationTemplateId = modalValidationTemplateGuid.value
      ? modalValidationTemplateGuid.value.trim().toUpperCase()
      : undefined;
    item.createLimitSetId = undefined;
  } else {
    item.createLimitSetId = modalLimitSetId.value
      ? modalLimitSetId.value.trim().toUpperCase()
      : undefined;
    item.createValidationTemplateId = undefined;
  }
  closeValidationLimitModal();
  updateGridRowData();
};

const onValidationLimitKindModalChanged = (evt: CustomEvent<string>) => {
  validationLimitKindForModal.value = evt.detail === 'true' ? 'limit' : 'validation';
};

const onValidationSearchChanged = (e: CustomEvent<string | null>) => {
  validationSearchTerm.value = (e.detail ?? '').trim();
};

const onValidationValueChanged = (e: CustomEvent<string | undefined>) => {
  const d = e.detail;
  if (d === undefined || d === null || d === '') {
    modalValidationTemplateGuid.value = undefined;
    return;
  }
  if (typeof d === 'string' && d.startsWith('folder:')) {
    return;
  }
  modalValidationTemplateGuid.value = d.trim().toUpperCase();
};

const onValidationSelectOption = (e: CustomEvent<any>) => {
  const option = e.detail;
  if (!option || option.hasChildren) {
    return;
  }
  let selected: string | null = null;
  if (option.value !== undefined && option.value !== null) {
    selected = String(option.value);
  }
  if (!selected || selected.startsWith('folder:')) {
    return;
  }
  modalValidationTemplateGuid.value = selected.trim().toUpperCase();
};

const onLimitSearchChanged = (e: CustomEvent<string | null>) => {
  limitSearchTerm.value = (e.detail ?? '').trim();
};

const onLimitValueChanged = (e: CustomEvent<string | undefined>) => {
  const d = e.detail;
  if (d === undefined || d === null || d === '') {
    modalLimitSetId.value = undefined;
    return;
  }
  if (typeof d === 'string' && d.startsWith('folder:')) {
    return;
  }
  modalLimitSetId.value = d.trim().toUpperCase();
};

const onLimitSelectOption = (e: CustomEvent<any>) => {
  const option = e.detail;
  if (!option || option.hasChildren) {
    return;
  }
  let selected: string | null = null;
  if (option.value !== undefined && option.value !== null) {
    selected = String(option.value);
  }
  if (!selected || selected.startsWith('folder:')) {
    return;
  }
  modalLimitSetId.value = selected.trim().toUpperCase();
};

const getDeleteCellRendererSelector = (params: any): CellRendererSelectorResult | undefined => {
  if (!canEdit.value) {
    return undefined;
  }
  
  return {
    component: (params: any) => {
      const deleteDiv = document.createElement('div');
      deleteDiv.classList.add('view-field', 'flex', 'items-center', 'justify-center', 'h-full');

      const icon = document.createElement('icon-delete');
      icon.classList.add('w-4', 'cursor-pointer');
      icon.color = 'red';
      icon.addEventListener('click', () => {
        handleDeleteTaskItem(params.data);
      });

      deleteDiv.appendChild(icon);
      return deleteDiv;
    }
  };
};

function getTaskTypeForItem(item: ITaskTypeTemplateItem | undefined) {
  const id = item?.taskTypeId;
  if (id === null || id === undefined || id === 0) return undefined;
  return taskTypesSource.value.find((t) => t.id === id);
}

function isAssignedToEditable(item: ITaskTypeTemplateItem | undefined): boolean {
  if (!canEdit.value) return false;
  const tt = getTaskTypeForItem(item);
  return tt?.approvalRequired !== true;
}

function applyTemplateItemDefaultsFromTaskType(item: ITaskTypeTemplateItem) {
  const id = item.taskTypeId;
  if (id === null || id === undefined || id === 0) {
    item.assignedTo = undefined;
    item.approvalRequired = undefined;
    return;
  }
  const tt = getTaskTypeForItem(item);
  if (tt === undefined || tt === null) {
    return;
  }
  const email = tt.assignedTo?.trim();
  item.assignedTo = email ? email : undefined;
  item.approvalRequired = tt.approvalRequired;
}

const handleAddTaskItem = () => {
  const newItem: ITaskTypeTemplateItem = {
    id: undefined,
    templateId: templateData.value.id ?? undefined,
    taskTypeId: undefined,
    createStatus: undefined,
    closeStatus: undefined,
    requiredForStatus: undefined,
    assignedTo: undefined,
    taskTypeTemplateItemClosed: [],
    createTaskTypeClosed: false,
    openRequirementKind: undefined,
    createValidationTemplateId: undefined,
    createLimitSetId: undefined,
    validationLimitResult: false,
  };
  templateItems.value.push(newItem);
};

const handleDeleteTaskItem = (item: ITaskTypeTemplateItem) => {
  const index = templateItems.value.findIndex(i => i === item);
  if (index > -1) {
    templateItems.value.splice(index, 1);
  }
};

const updateGridRowData = () => {
  if (gridRef.value) {
    gridRef.value.rowData = [...templateItems.value];
  }
};

function itemHasClosedTasks(item: ITaskTypeTemplateItem): boolean {
  if (item.taskTypeTemplateItemClosed === undefined || item.taskTypeTemplateItemClosed === null) {
    return false;
  }
  return item.taskTypeTemplateItemClosed?.length > 0;
}

function itemHasOpenTradeStatus(item: ITaskTypeTemplateItem): boolean {
  const s = item.createStatus;
  if (s === undefined || s === null) {
    return false;
  }
  return !(typeof s === 'string' && s.trim() === '');
}

function validateAll(): void {
  const errors: Validations = {};

  if (!templateData.value.templateName || templateData.value.templateName.trim() === '') {
    errors.templateName = 'Template name is required';
  }

  if (!templateData.value.description || templateData.value.description.trim() === '') {
    errors.description = 'Description is required';
  }

  if (templateItems.value.length === 0) {
    errors.templateitems = 'You have to add at least one template rule.'
  }

  if (templateItems.value.some(rule => rule.assignedTo === undefined || rule.assignedTo === '' || rule.assignedTo === null)) {
    errors.templateitems = 'All rules have to have Assigned to populated.'
  }

  if (
    templateItems.value.some(
      (rule) => !itemHasOpenTradeStatus(rule) && !itemHasClosedTasks(rule)
    )
  ) {
    errors.templateitems =
      'Each rule must have Open Trade status or at least one task under Tasks Closed.';
  }

  if (
    templateItems.value.some((rule) => {
      const hasValidationOrLimit =
        Boolean(rule.createValidationTemplateId?.trim()) || Boolean(rule.createLimitSetId?.trim());
      return hasValidationOrLimit && !itemHasClosedTasks(rule);
    })
  ) {
    errors.templateitems =
      'Rules with a Validation or Limit must have at least one task selected under Tasks Closed.';
  }

  if (templateItems.value.some(rule => rule.taskTypeId === 0 || rule.taskTypeId === undefined)) {
    errors.templateitems = 'All rules have to have Task Type populated.'
  }

  validations.value = errors;
  if (errors) {
    showValidations.value = true;
  }
}

const overrideGridOptions = (evt: CustomEvent<Returnable<Partial<GridOptions>>>) => {
  evt.detail.returns({
    onCellClicked: (e: any) => {
      if (!canEdit.value) {
        return;
      }
      if (e.column?.getColId() !== 'validationLimit') {
        return;
      }
      const data = e.data as ITaskTypeTemplateItem;
      const rowIdx = templateItems.value.indexOf(data);
      if (rowIdx < 0) {
        return;
      }
      openValidationLimitModal(rowIdx);
    },
    columnDefs: [
      {
        field: 'taskTypeId',
        headerName: 'Task Type',
        width: 150,
        editable: canEdit.value,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        cellEditor: TaskTypePickerCellEditor,
        cellEditorParams: () => ({
          getRows: () => taskTypePickerRows.value,
          placeholder: 'Select task type...',
          getValue: (data: unknown) => (data as ITaskTypeTemplateItem | undefined)?.taskTypeId,
        }),
        valueGetter: (params: any) => {
          const taskTypeId = params.data?.taskTypeId;
          if (taskTypeId === null || taskTypeId === undefined || taskTypeId === 0) {
            return 'Select a task type...';
          }
          return (
            taskTypeIdToLabel.value.get(taskTypeId) ??
            taskTypesSource.value.find((x) => x.id === taskTypeId)?.name ??
            'Select a task type...'
          );
        },
        valueSetter: (params: any) => {
          const item = params.data as ITaskTypeTemplateItem | undefined;
          if (!item) {
            return false;
          }
          const newValue = params.newValue;
          if (newValue === null || newValue === undefined || newValue === '') {
            return false;
          }
          if (typeof newValue === 'string' && newValue.startsWith('group:')) {
            return false;
          }
          const taskTypeId = typeof newValue === 'number' ? newValue : Number(newValue);
          if (Number.isNaN(taskTypeId) || taskTypeId === 0) {
            return false;
          }
          item.taskTypeId = taskTypeId;
          applyTemplateItemDefaultsFromTaskType(item);
          if (params.node && params.api) {
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: ['approvalRequired', 'assignedTo'],
              force: true,
            });
          }
          return true;
        },
      },
      {
        headerName: "Open Task When",
        children: [
          {
            field: "createStatus",
            headerName: "Trade Status",
            width: 120,
            editable: canEdit.value,
            suppressHeaderMenuButton: true,
            cellEditor: "dropdownInputCellEditor",
            cellEditorParams: statusEditorParams.value,
            valueGetter: (params: any) => {
              return params.data?.createStatus || null;
            },
          },
          {
            field: "taskTypeTemplateItemClosed",
            headerName: "And Tasks Closed",
            width: 150,
            editable: canEdit.value,
            suppressHeaderMenuButton: true,
            cellEditor: "multiDropdownInputCellEditor",
            cellEditorParams: () => ({
              source: taskTypesSource.value,
              hidePills: true,
              textField: 'name',
              valueField: 'id',
              enableSearch: true,
              placeholder: 'Type to search...',
            }),
            valueGetter: (params: any) => params.data?.taskTypeTemplateItemClosed.map((x: ITaskTypeTemplateItemClosed) => x.taskTypeId),
            valueSetter: (params: any) => {
              const item = params.data as ITaskTypeTemplateItem;
              if (item === undefined || item === null) return false;

              item.taskTypeTemplateItemClosed = (params.newValue as number[]).map(
                id => ({ taskTypeId: id } as ITaskTypeTemplateItemClosed)
              );
              return true;
            },
            valueFormatter: (params: any) => {
              if (params.value === undefined || params.value === null || params.value.length === 0) return '';
              return (params.value as number[])
                .map(id => taskTypesSource.value.find(t => t.id === id)?.name)
                .join(', ');
            },
          },
          {
            colId: 'validationLimit',
            headerName: 'Validations/Limits',
            width: 180,
            editable: false,
            suppressHeaderMenuButton: true,
            cellStyle: canEdit.value ? { cursor: 'pointer' } : undefined,
            valueGetter: (params: any) => formatValidationLimitSummary(params.data),
          },
          {
            field: "validationLimitResult",
            headerName: "Limit/Validation Result",
            width: 190,
            editable: canEdit.value,
            suppressHeaderMenuButton: true,
            cellRenderer: "toggleRenderer",
            cellRendererParams: () => ({
              trueLabel: "pass",
              falseLabel: "fail",
              disabled: !canEdit.value,
            }),
            valueGetter: (params: any) => params.data?.validationLimitResult ?? false,
          },
        ],
      },
      {
        field: 'closeStatus',
        headerName: 'Close When Trade Status',
        width: 140,
        editable: canEdit.value,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        cellEditor: 'dropdownInputCellEditor',
        cellEditorParams: statusEditorParams.value,
        valueGetter: (params: any) => { return params.data?.closeStatus || null; }
      },
      {
        field: "requiredForStatus",
        headerName: "Required to Proceed to Status",
        width: 150,
        editable: canEdit.value,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        cellEditor: 'dropdownInputCellEditor',
        cellEditorParams: statusEditorParams.value,
        valueGetter: (params: any) => {
          return params.data?.requiredForStatus || null;
        },
      },
      {
        colId: 'approvalRequired',
        headerName: "Approval Required?",
        width: 120,
        editable: false,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        valueGetter: (params: any) => {
          const tt = getTaskTypeForItem(params.data);
          return tt ? tt.approvalRequired : null;
        },
        valueFormatter: (params: any) => (params.value == null ? '' : params.value ? 'Yes' : 'No'),
      },
      {
        field: 'assignedTo',
        headerName: 'Assigned To',
        flex: 1,
        editable: (params: any) => isAssignedToEditable(params.data),
        minWidth: 150,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        cellEditor: 'dropdownInputCellEditor',
        cellEditorParams: {
          source: userSource.value,
          textField: 'username',
          valueField: 'email',
          width: '100%',
          minWidth: 200,
          enableSearch: true,
          placeholder: 'Select user...',
        }
      },
      {
        headerName: '',
        sortable: false,
        resizable: false,
        pinned: 'right',
        width: 60,
        suppressHeaderMenuButton: true,
        cellRendererSelector: getDeleteCellRendererSelector
      }
    ]
  });
};

const onIsDealChanged = (evt: CustomEvent<string>) => {
  templateData.value.isDeal = evt.detail === 'true';
  templateItems.value.forEach(item => {
    item.createStatus = undefined;
    item.closeStatus = undefined;
    item.requiredForStatus = undefined;
  });

  refreshStatusColumns();
}

const refreshStatusColumns = () => {
  gridRef.value?.getGridApi().then(gridApi => {
    if (gridApi === undefined || gridApi === null) return;

    const statusColumns = ['createStatus', 'closeStatus', 'requiredForStatus'];

    statusColumns.forEach(columnName => {
      const colDef = gridApi.getColumnDef(columnName);
      if (colDef) {
        colDef.cellEditorParams = statusEditorParams.value;
      }
    });

    gridApi.refreshCells({ columns: statusColumns });
  });
}

const handleSave = async () => {
  validateAll();
  showValidations.value = true;

  if (hasErrors.value) {
    return;
  }

  window.loading?.('Saving template...');
  try {
    const helper = new AjaxHelper('', props.verificationToken, null, token.value);

    const saveData = {
      id: templateData.value.id,
      templateName: templateData.value.templateName,
      description: templateData.value.description,
      isDeal: templateData.value.isDeal,
      taskTypeTemplateItems: templateItems.value
    };

    const result = await helper.postData<ITaskTypeTemplate>('/componentapi/transaction/template', saveData);
    if (result?.id) {
      templateData.value.id = result.id;
    }

    window.showAlert?.success('Template saved successfully');
    emits('saved');
  } catch (error: any) {
    console.error('Failed to save template:', error);
    if (error.response !== undefined && error.response.status === 400 && error.data !== undefined) {
      window.showAlert?.error('Failed to save template', error.data?.errors[0].message);
    } else {
      window.showAlert?.error('Failed to save template');
    }
  } finally {

    window.loading?.();
  }
};

const handleCancel = () => {
  emits('cancelled');
};

watch([() => templateData.value, () => templateItems.value, showValidations], () => {
  if (showValidations.value) {
    validateAll();
  }
}, { deep: true });

watch(() => templateItems.value, (newValue) => {
  updateGridRowData();
}, { deep: true });

onMounted(async () => {
  await Promise.all([taskTypesSource.value.read(), taskGroupsSource.value.read()]);
  await userSource.value.read();

  try {
    domainLimitsSource.value = new DomainLimitsSource(
      domainSetPickerApiRoot.value,
      props.verificationToken,
      token.value
    );
    await domainLimitsSource.value.read();
  } catch (err) {
    console.error('Failed to load domain limits tree', err);
  } finally {
    limitsPickerLoaded.value = true;
  }

  try {
    validationTemplatesSource.value = new ValidationTemplatesSource(
      domainSetPickerApiRoot.value,
      props.verificationToken,
      token.value
    );
    await validationTemplatesSource.value.read();
  } catch (err) {
    console.error('Failed to load validation templates tree', err);
  } finally {
    validationsPickerLoaded.value = true;
  }

  let assignedToSync = [];
  for (const item of templateItems.value) {
    const tt = getTaskTypeForItem(item);
    if (tt?.approvalRequired !== true) {
      continue;
    }
    const newAssignedTo = tt.assignedTo?.trim().toLowerCase();
    const previousAssignedTo = item.assignedTo?.trim().toLowerCase() || undefined;
    if (previousAssignedTo === newAssignedTo) {
      continue;
    }
    item.assignedTo = newAssignedTo;
    assignedToSync.push(tt);
  }
  if (assignedToSync.length > 0) {
    window.showAlert?.warning(
      'Assigned To updated',
      assignedToSync.length === 1
        ? `Assigned To was updated for the Template Item for Task Type "${assignedToSync[0].name}".`
        : `Assigned To was updated for ${assignedToSync.length} Template Items.`
    );
  }

  refreshStatusColumns();
  updateGridRowData();
});

return { __sfc: true,_blankTemplate, createInitialTemplate, props, emits, token, user, showValidations, validations, templateData, templateItems, taskTypesSource, taskGroupsSource, userSource, gridRef, domainLimitsSource, limitsPickerLoaded, limitSearchTerm, validationTemplatesSource, validationsPickerLoaded, validationSearchTerm, getStatusOptions, statusEditorParams, isNew, hasErrors, templateNameError, descriptionError, templateItemsError, entitledToCreateTaskTemplates, entitledToEditOrRemoveTaskTemplates, canEdit, taskTypePickerRows, taskTypeIdToLabel, domainSetPickerApiRoot, limitsPickerRows, filteredLimitPickerRows, limitGuidToLabel, limitLabelForGuid, validationPickerRows, filteredValidationPickerRows, validationGuidToLabel, validationLabelForGuid, validationLimitModalOpen, validationLimitEditingIndex, validationLimitKindForModal, modalValidationTemplateGuid, modalLimitSetId, formatValidationLimitSummary, openValidationLimitModal, closeValidationLimitModal, applyValidationLimitModal, onValidationLimitKindModalChanged, onValidationSearchChanged, onValidationValueChanged, onValidationSelectOption, onLimitSearchChanged, onLimitValueChanged, onLimitSelectOption, getDeleteCellRendererSelector, getTaskTypeForItem, isAssignedToEditable, applyTemplateItemDefaultsFromTaskType, handleAddTaskItem, handleDeleteTaskItem, updateGridRowData, itemHasClosedTasks, itemHasOpenTradeStatus, validateAll, overrideGridOptions, onIsDealChanged, refreshStatusColumns, handleSave, handleCancel }
}

})